import React from "react";
import cx from "classnames";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { getHoverTipContent } from "@/Utils/Misc";
import { getPlanPermissions } from "@/Utils/getPlanPermissions";
import { Flex, Icon, OverflowText, Text } from "@theorgchartdev/storybook";
import ToolbarPopover from "@/appComponents/Navigation/ContextualToolbar/ToolbarPopover/ToolbarPopover";
import {
  enumHierarchyTypes,
  enumObjectTypes,
  enumOverrideType,
  kNameID,
  objTypeClassMap
} from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { appstrp } from "@coreapp/utils/appstrp";
import { QPlanManager } from "@coreapp/CoreApp/QPlanManager";
import { QChange } from "@coreapp/CoreApp/QChange";
import { convertStdDateString } from "@coreapp/utils/convertStdDateString";

const MultipleChangesPopover = ({ changeDisplayItem, openCtrlRef }) => {
  return (
    <ToolbarPopover
      icon="circle-info"
      iconOnly
      placement="bottom-start"
      otherPopoverProps={{ openCtrlRef }}
      overlayProps={{
        sizeProps: {
          maxWidth: 300,
          maxHeight: 200
        },
        padding: "12px"
      }}
    >
      <Flex direction="column" gap="8px">
        <Text size="small" modifier="bold">
          {changeDisplayItem.title}
        </Text>
        <ul className="oc-list-disc oc-margin-0 oc-padding-left-1">
          {changeDisplayItem.allChanges.map(change => (
            <li key={change.GetOverride()?.SmartGetFieldValue("RefID")}>
              <Text size="xsmall">{change.GetChangeEmployee()}</Text>
            </li>
          ))}
        </ul>
      </Flex>
    </ToolbarPopover>
  );
};

export const GetPlanActivityMismatchActions = ({ mismatch, changeHandlers }) => {
  const id = GetIdFromMismatch(mismatch);

  const wsMgr = GetWSMgr();
  const planMgr = wsMgr?.fPlanManager;

  const { canEdit } = getPlanPermissions({ useCurrentPlan: true });

  return [
    {
      active: true,
      dataTestId: id + "-mismatchEditAction",
      iconName: "circle-exclamation",
      iconProps: { color: "error" },
      tooltipProps: { message: appstr("Resolve conflict") },
      disabled: !canEdit,
      onClick: () => {
        // Check if the associated override exists
        let override = GetUnfilteredActiveOverrides(planMgr).find(
          o => o.SmartGetFieldValue("RefID") === id
        );
        if (!override) {
          const doc = wsMgr.GetDoc();

          let objType = enumObjectTypes.holder;
          if (doc.GetHierarchyType() === enumHierarchyTypes.positions) {
            objType = enumObjectTypes.position;
          }
          override = planMgr.CreateOverride(enumOverrideType.change, objType, id, "", []);
        }

        changeHandlers.handleEdit(override, true);
      }
    }
  ];
};

export const BuildPlanActivityOverrideActions = ({
  changeDisplayItem,
  override,
  changeHandlers,
  openCtrlRef
}) => {
  const actions = [];

  let dataId = "";
  if (override) {
    dataId = override.SmartGetFieldValue("RefID") + "-" + override.SmartGetFieldValue("Type");
  }

  if (changeDisplayItem) {
    dataId = changeDisplayItem.title;
  }

  const wsMgr = GetWSMgr();
  const planMgr = wsMgr?.fPlanManager;
  const canEdit = planMgr?.CanUpdatePlan() ?? false;

  const item = override ?? changeDisplayItem;

  if (planMgr && DoesItemHaveConflict(item, planMgr)) {
    return [
      {
        active: true,
        dataTestId: dataId + "-resolveConflictAction",
        iconName: "circle-exclamation",
        disabled: !canEdit,
        iconProps: { color: "error" },
        tooltipProps: { message: appstr("Resolve conflict") },
        onClick: () => changeHandlers.handleConflict(item)
      }
    ];
  }

  const multiRecordChange = changeDisplayItem?.allChanges?.length > 1;

  if (multiRecordChange) {
    actions.push({
      checkActive: () => openCtrlRef?.current?.open ?? false,
      content: (
        <MultipleChangesPopover changeDisplayItem={changeDisplayItem} openCtrlRef={openCtrlRef} />
      )
    });
  } else {
    actions.push({
      iconName: "sitemap",
      tooltipProps: getHoverTipContent("Navigate to record in plan"),
      onClick: () => changeHandlers.handleNavigation(item),
      dataTestId: dataId + "-navigateAction"
    });
  }

  if (canEdit) {
    const shouldShowEdit = checkIfShouldShowEdit(item);
    if (shouldShowEdit) {
      actions.push({
        iconName: "pen-to-square",
        tooltipProps: getHoverTipContent("Open change"),
        onClick: () => changeHandlers.handleEdit(item),
        dataTestId: dataId + "-editAction",
        ...(multiRecordChange &&
          openCtrlRef && { checkActive: () => openCtrlRef?.current?.open ?? false })
      });
    }

    const shouldShowReopen = checkIfShouldShowReopen(item, planMgr);
    if (shouldShowReopen) {
      actions.push({
        iconName: "rotate-right",
        tooltipProps: getHoverTipContent("Reopen position multi"),
        onClick: () => changeHandlers.handleReopen(item),
        dataTestId: dataId + "-reopenAction",
        ...(multiRecordChange &&
          openCtrlRef && { checkActive: () => openCtrlRef?.current?.open ?? false })
      });
    }
  }

  return [...actions];
};

export const GetUnfilteredActiveOverrides = planMgr => {
  let doc = GetDoc();
  let options = [];
  if (!doc || !planMgr) return options;
  let overrideClass = doc.GetObjDB().GetClassByName("Override");
  let overrides = overrideClass.GetObjectList();

  return overrides.filter(function (override) {
    return override.lastActiveChange() != null;
  });
};

export const GetFilteredAndSortedOverrides = (planMgr, filters = [], searchKey = null) => {
  let doc = GetDoc();
  let options = [];
  if (!doc || !planMgr) return options;

  let overrides = GetUnfilteredActiveOverrides(planMgr);
  let mismatches = [];

  const conflictsFilter = filters.indexOf("Show conflicts only") > -1;

  if (conflictsFilter) {
    overrides = planMgr.fTrackList?.GetFilteredObjList();
  }

  // hide aux-overrides
  overrides = overrides.filter(function (override) {
    const displayObj = override.SmartGetFieldValue("displayObject");
    const isAuxValue = displayObj?.SmartGetFieldValue("isAux");
    const isAuxInstance = isAuxValue > 0;

    return !isAuxInstance;
  });

  if (!filters.includes("Value Mismatch")) {
    mismatches = planMgr.GetPickListMismatch();
  }

  var changeTypeList = overrides.map(function (item) {
    return item.SmartGetFieldValue("Type");
  });

  changeTypeList = changeTypeList.filter(function (value, index, array) {
    return array.indexOf(value) === index;
  });

  changeTypeList.sort(function (a, b) {
    return a.localeCompare(b);
  });

  // type and search filtering
  overrides = overrides.filter(function (value) {
    return !filters.includes(value.SmartGetFieldValue("Type"));
  });

  if (searchKey !== "" && searchKey !== null)
    overrides = overrides.filter(override => FilterValues(override, searchKey));

  return { overrides, mismatches };
};

export const FilterValues = (override, string) => {
  const curDoc = GetDoc();
  const objDB = curDoc.GetObjDB();
  const displayObj =
    override instanceof QObjDBObject ? override.SmartGetFieldValue("displayObject") : override;
  if (!displayObj) return;

  const objType = override.SmartGetFieldValue("objType") ?? enumObjectTypes.holder;
  const className = objTypeClassMap[objType];
  const parentClass = objDB.GetClassByName(className);

  const recordIDDef = parentClass.GetRecordIDFieldDef();
  const parentIDDef = parentClass.GetParentIDFieldDef();
  const nameDef = parentClass.GetFieldDefByID(kNameID);
  const titleDef = parentClass.GetTitleFieldDef();

  const values = [recordIDDef, parentIDDef, nameDef, titleDef]
    .filter(def => def) // Some definitions will not be present. For example, positions will not have a Name
    .map(def => {
      return (displayObj.GetFieldValue(def) || "").toLowerCase();
    });

  return values.some(value => {
    return value.includes(string.toLowerCase().trim());
  });
};

export const GetChangeAccordionLabel = (name, type, includeIcon) => {
  return (
    <Flex gap={"4px"} padding={"0 0 0 4px"}>
      {includeIcon && <div className="oc-shrink-0">{GetGroupIconByType(type)}</div>}
      <OverflowText clamp={1} modifier={"bold"} size={"small"}>
        {name}
      </OverflowText>
    </Flex>
  );
};

export const GetCCChangeAccordionLabel = (name, isPosition) => {
  return (
    <Flex gap={"4px"} padding={"0 0 0 4px"}>
      <div className="oc-shrink-0">
        <Icon name={isPosition ? "cubes" : "image-user"} size="small" />
      </div>
      <OverflowText clamp={1} modifier={"bold"} size={"small"}>
        {name}
      </OverflowText>
    </Flex>
  );
};

export const GetChangeTypeIconProps = (changeType, otherProps) => {
  let name = "";
  let modifier = "default";
  switch (changeType) {
    case enumOverrideType.add:
      name = "plus-solid";
      modifier = "green";
      break;
    case enumOverrideType.change:
      name = "pen";
      modifier = "yellow";
      break;
    case enumOverrideType.move:
      name = "arrow-right-solid";
      modifier = "blue";
      break;
    case enumOverrideType.moveout:
      name = "arrow-left-from-line-solid";
      modifier = "purple";
      break;
    case enumOverrideType.comment:
      name = "comment";
      modifier = "white";
      break;
    case enumOverrideType.lock:
      name = "lock";
      modifier = "default";
      break;
    case enumOverrideType.delete:
      name = "xmark-solid";
      modifier = "red";
      break;
    case enumObjectTypes.position:
      name = "cubes";
      modifier = "default";
      break;
    case enumObjectTypes.holder:
      name = "image-user";
      modifier = "default";
      break;
    default:
      break;
  }

  const className = cx("oc-plan-icon", "oc-plan-icon--" + modifier);
  return {
    name,
    className,
    ...otherProps
  };
};

export const GetGroupIconByType = (iconType, otherIconProps) => {
  const iconProps = { ...GetChangeTypeIconProps(iconType), otherIconProps };
  return <Icon size={"small"} {...iconProps} />;
};

export const GetLabelFromMismatch = mismatch => {
  const id = GetIdFromMismatch(mismatch);
  const wsMgr = GetWSMgr();
  const planMgr = wsMgr?.fPlanManager;
  const dataObj = planMgr?.fCloneBaseCache[id];
  if (dataObj) {
    return dataObj.FormatEmployeeLabel();
  }

  const curDoc = GetDoc();
  const objDB = curDoc.GetObjDB();
  const boxClass = objDB.GetClassByName("Box");
  const nameDef = boxClass.GetFieldDefByID("Name");

  return mismatch[nameDef.GetFieldName()];
};

export const GetIdFromMismatch = mismatch => {
  const curDoc = GetDoc();
  const objDB = curDoc.GetObjDB();
  const boxClass = objDB.GetClassByName("Box");
  const idDef = boxClass.GetFieldDefByID("PersonID");

  return mismatch[idDef.GetFieldName()];
};

export const GetIdFromMismatchOrOverride = ({ override, mismatch }) => {
  if (mismatch) {
    return GetIdFromMismatch(mismatch);
  }

  return override.SmartGetFieldValue("RefID");
};

/**
 * @typedef {Object} AccordionGroup
 * @property {string} name
 * @property {Array<(QObjDBObject | object)>} children
 * @property {string} value
 */

/**
 *
 * @param {string} name
 * @param {Array<(QObjDBObject | object)>} children
 * @param {string} id
 *
 * @returns {AccordionGroup}
 */
export const NormalizeAccordionGroup = (name, children, id) => {
  return {
    name: name,
    children: children,
    value: id
  };
};

/**
 * @typedef {Object} ChangeDisplayGroup
 * @property {string} title
 * @property {string} icon
 * @property {string} value
 * @property {(ChangeDisplayItem | MismatchDisplayItem)[]} children
 */

/**
 * @typedef {Object} ChangeDisplayItem
 * @property {'changes'} type
 * @property {string} title
 * @property {string} icon
 * @property {object[]} actions
 * @property {QChange[]} changes - Only the changes that should be rendered in the UI
 * @property {QChange[]} allChanges - All changes that are related to a given change
 */

/**
 * @typedef {Object} MismatchDisplayItem
 * @property {'mismatch'} type
 * @property {string} title
 * @property {object[]} actions
 * @property {object} mismatch
 */

/**
 * @param {QObjDBObject[]} overrides
 * @param {*} changeHandlers
 * @param {Function} createRef
 *
 * @returns {Array<ChangeDisplayGroup>}
 */
export const GroupChangesForHistory = (overrides, changeHandlers, createRef = null) => {
  // Create a flat list of items
  const mappedItems = overrides.reduce((acc, override) => {
    const refId = override.SmartGetFieldValue("RefID");
    const objType = override.SmartGetFieldValue("objType") ?? enumObjectTypes.holder;

    const changes = override.fChanges ?? [];

    for (const change of changes) {
      const actionID = change.GetActionID();

      // Conflicted changes should not be rolled up, so
      // conflict resolution can happen on individual records
      if (actionID && !change.IsConflicted()) {
        const binID = `${actionID}-${objType}`;

        const actionChanges = acc[binID]?.allChanges ?? [];
        actionChanges.push(change);

        let changeTitle = "";
        if (actionChanges.length === 1) {
          changeTitle = change.GetChangeEmployee();
        } else if (objType === enumObjectTypes.position) {
          changeTitle = `${appstr("Multiple positions")} (${actionChanges.length})`;
        } else {
          changeTitle = `${appstr("Multiple employees")} (${actionChanges.length})`;
        }

        const binDate = change.GetSortableDate();

        acc[binID] = {
          type: "changes",
          title: changeTitle,
          icon: objType,
          changes: [change],
          allChanges: actionChanges,
          date: binDate,
          objType: objType,
          group: binDate.FormatDate(0)
        };

        continue;
      }

      const effectiveDate = change.GetSortableDate();
      const changeID = `${refId}-${objType}-${effectiveDate.getTime()}`;
      acc[changeID] = {
        type: "changes",
        title: change.GetChangeEmployee(),
        icon: objType,
        changes: [change],
        allChanges: [change],
        date: effectiveDate,
        objType: objType,
        group: effectiveDate.FormatDate(0)
      };
    }

    return acc;
  }, {});

  // overrides are already stored in order, reversing the order to see latest changes first
  const items = Object.values(mappedItems).reverse();

  // Create groups from the flat list by their group
  const mappedGroups = items.reduce((acc, item) => {
    const itemGroup = acc[item.group] ?? {
      title: item.group,
      icon: item.objType,
      children: []
    };

    const actionId = item.allChanges?.[0]?.GetActionID();
    const openCtrlRef = createRef && actionId ? createRef(actionId) : undefined;
    item.actions = BuildPlanActivityOverrideActions({
      changeDisplayItem: item,
      changeHandlers,
      openCtrlRef
    });

    itemGroup.children.push(item);
    acc[item.group] = itemGroup;
    return acc;
  }, {});

  const groups = Object.values(mappedGroups);

  return groups;
};

/**
 *
 * @param {QObjDBObject[]} overrides
 * @param {QObjDBObject[]} mismatches
 */
export const GroupOverridesByChangeType = (overrides, mismatches) => {
  const accordionGroups = [];

  // get unique type list
  let currentTypeList = overrides.map(function (item) {
    return item.SmartGetFieldValue("Type");
  });
  currentTypeList = [...new Set(currentTypeList)];

  // alphabetize the change type bins
  currentTypeList.sort(function (a, b) {
    return a.localeCompare(b);
  });

  for (let overrideType of currentTypeList) {
    let childrenOverrides = overrides.filter(function (item) {
      return item.SmartGetFieldValue("Type") === overrideType;
    });
    let groupLabel = appstrp(overrideType, "s") + " (" + childrenOverrides.length + ")";
    let newGroup = NormalizeAccordionGroup(groupLabel, childrenOverrides, overrideType);
    accordionGroups.push(newGroup);
  }

  if (mismatches.length > 0) {
    const groupLabel = appstr("Value mismatches") + " (" + mismatches.length + ")";
    const newGroup = NormalizeAccordionGroup(groupLabel, mismatches, "Value Mismatch");
    accordionGroups.push(newGroup);
  }

  return accordionGroups;
};

/**
 *
 * @param {QObjDBObject[]} overrides
 * @param {QObjDBObject[]} mismatches
 */
export const GroupOverridesByObjType = (overrides, mismatches) => {
  const accordionGroups = [];

  const objTypeMap = overrides.reduce((acc, override) => {
    const objType = override.SmartGetFieldValue("objType");

    if (!acc[objType]) {
      acc[objType] = [];
    }

    acc[objType].push(override);
    return acc;
  }, {});

  const sortedObjTypes = Object.keys(objTypeMap).sort((a, b) => {
    return a.localeCompare(b);
  });

  const labelMap = {
    [enumObjectTypes.holder]: appstr("Employee changes"),
    [enumObjectTypes.position]: appstr("Position changes")
  };

  const holderOverrides = overrides.filter(item => {
    return item.SmartGetFieldValue("objType") === enumObjectTypes.holder;
  });

  if (holderOverrides.length > 0) {
    holderOverrides.sort((aChild, bChild) => {
      const aChange = aChild.lastActiveChange();
      const bChange = bChild.lastActiveChange();

      const aLabel = aChange?.GetChangeEmployee() ?? "";
      const bLabel = bChange?.GetChangeEmployee() ?? "";

      return aLabel.localeCompare(bLabel);
    });

    const holderGroup = NormalizeAccordionGroup(
      labelMap[enumObjectTypes.holder],
      holderOverrides,
      enumObjectTypes.holder
    );

    accordionGroups.push(holderGroup);
  }

  const positionOverrides = overrides.filter(item => {
    return item.SmartGetFieldValue("objType") === enumObjectTypes.position;
  });

  if (positionOverrides.length > 0) {
    positionOverrides.sort((aChild, bChild) => {
      const aChange = aChild.lastActiveChange();
      const bChange = bChild.lastActiveChange();

      const aLabel = aChange?.GetChangeEmployee() ?? "";
      const bLabel = bChange?.GetChangeEmployee() ?? "";

      return aLabel.localeCompare(bLabel);
    });

    const positionGroup = NormalizeAccordionGroup(
      labelMap[enumObjectTypes.position],
      positionOverrides,
      enumObjectTypes.position
    );

    accordionGroups.push(positionGroup);
  }

  if (mismatches.length > 0) {
    const groupLabel = appstr("Value mismatches") + " (" + mismatches.length + ")";
    const newGroup = NormalizeAccordionGroup(groupLabel, mismatches, "Value Mismatch");
    accordionGroups.push(newGroup);
  }

  return accordionGroups;
};

/**
 *
 * @param {Array<QObjDBObject>} overrides
 * @param {QPlanManager} planMgr
 * @returns {Array<AccordionGroup>}
 */
export const GroupOverridesForHistory = (overrides, planMgr) => {
  let binDay = {};
  // sort by change date
  const displayOverrides = overrides.toSorted((a, b) => {
    return planMgr.SortChangeDate(a, b);
  });

  displayOverrides.forEach(function (override) {
    // bin by day
    let changeObj = override.lastActiveChange();
    if (!changeObj) return false;
    let dateString = changeObj.GetChangeDate();
    let d = new Date(convertStdDateString(dateString).toDateString());
    binDay[d] = binDay[d] || [];
    binDay[d].push(override);
  });

  const accordionGroups = Object.keys(binDay).map(binID => {
    let groupLabel = new Date(binID).GetBinIDFromDate(binDay, true);
    let childrenOverrides = binDay[binID];
    return NormalizeAccordionGroup(groupLabel, childrenOverrides, groupLabel);
  });

  return accordionGroups;
};

/**
 *
 * @param {(AccordionGroup | ChangeDisplayGroup)} group
 * @param {QPlanManager} planMgr
 * @returns {boolean}
 */
export const DoesGroupHaveConflict = (group, planMgr) => {
  return group.children.some(item => {
    return DoesItemHaveConflict(item, planMgr);
  });
};

/**
 *
 * @param {(ChangeDisplayItem | QObjDBObject)} item
 * @param {QPlanManager} planMgr
 * @returns {boolean}
 */
export const DoesItemHaveConflict = (item, planMgr) => {
  if (item instanceof QObjDBObject) {
    return planMgr.OverrideHasConflicts(item);
  }

  if (item.type === "changes") {
    return item.changes.some(change => {
      return change.IsConflicted();
    });
  }

  return false;
};

/**
 * Check if a position is closed by checking if the override change type is delete
 * @param {QObjDBObject} override
 * @returns {boolean}
 */
const isPositionClosed = override => {
  if (!override) return false;

  const objType = override.SmartGetFieldValue("objType");
  if (objType !== enumObjectTypes.position) return false;

  const changeType = override.SmartGetFieldValue("Type");
  return changeType === enumOverrideType.delete;
};

/**
 * Check if edit action should be shown for the given item
 * @param {(ChangeDisplayItem | QObjDBObject)} item
 * @returns {boolean}
 */
const checkIfShouldShowEdit = item => {
  if (item.type === "changes") {
    // For multiple changes
    const firstObjType = item.allChanges[0]?.GetOverride()?.SmartGetFieldValue("objType");

    if (firstObjType === enumObjectTypes.position) {
      // Show edit for positions if at least one is non-closed
      return item.allChanges.some(change => {
        const override = change.GetOverride();
        return !isPositionClosed(override);
      });
    } else if (firstObjType === enumObjectTypes.holder) {
      // Only show edit for single holder, not multiple
      return item.allChanges.length === 1;
    }

    return false;
  } else {
    // Single override case
    const objType = item.SmartGetFieldValue("objType");

    // Show edit for single holders or non-closed positions
    if (objType === enumObjectTypes.position) {
      return !isPositionClosed(item);
    }
    return objType === enumObjectTypes.holder;
  }
};

/**
 * Check if reopen action should be shown for the given item
 * @param {(ChangeDisplayItem | QObjDBObject)} item
 * @param {QPlanManager} planMgr
 * @returns {boolean}
 */
const checkIfShouldShowReopen = (item, planMgr) => {
  if (!planMgr) return false;

  if (item.type === "changes") {
    // For multiple changes, show reopen if at least one position is closed
    return item.allChanges.some(change => {
      const override = change.GetOverride();
      return isPositionClosed(override);
    });
  } else {
    // Single override case
    return isPositionClosed(item);
  }
};
